import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import {
  markNotificationAsReadAction,
  markAllNotificationsAsReadAction,
  clearAllNotificationsAction,
  removeNotificationAction
} from '../../connector/notifications';
import {
  CallbackIcon,
  CallIcon,
  CasesIcon,
  ChatIcon,
  ChromeNotificationsIcon,
  VoicemailIcon,
  speakerIconSVG,
  BellIconPlain
} from '../common/Icons';
import { ActionButton, Content, Dialog, Divider, Flex, Text, View } from '@adobe/react-spectrum';
import Close from '@spectrum-icons/workflow/Close';
import './style.css';

// Simple notifications display component for dialog box
const NotificationsPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useSelector((state: any) => state.notifications);

  const handleMarkAsRead = (id: string) => {
    dispatch(markNotificationAsReadAction(id));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationsAsReadAction(null));
  };

  const handleClearAll = () => {
    dispatch(clearAllNotificationsAction(null));
  };

  const handleRemoveNotification = (id: string) => {
    dispatch(removeNotificationAction(id));
  };

  const formatTimestamp = (timestamp: string) => {
    const notificationDate = new Date(timestamp);
    const now = new Date();
    
    // Calculate the difference in days
    const diffTime = now.getTime() - notificationDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // If today, show only time
    if (diffDays === 0 && now.toDateString() === notificationDate.toDateString()) {
      return notificationDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    
    // If yesterday, show "Yesterday" + time
    if (diffDays === 1) {
      return `Yesterday, ${notificationDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;
    }
    
    // If older than yesterday, show date and time
    return notificationDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: now.getFullYear() !== notificationDate.getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getNotificationIcon = (type: string, category?: string) => {
    switch (type) {
      case 'call':
        return CallIcon;
      case 'chat':
        return ChatIcon;
      case 'task':
        return CasesIcon;
      case 'case':
        return CasesIcon;
      case 'voicemail':
        return VoicemailIcon;
      case 'callback':
        return CallbackIcon;
      case 'system':
        // Use different icons based on category
        if (category === 'audio') {
          return speakerIconSVG;
        } else if (category === 'permissions') {
          return ChromeNotificationsIcon;
        }
        return BellIconPlain;
      default:
        return BellIconPlain;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'call':
        return '#e74c3c';
      case 'chat':
        return '#3498db';
      case 'task':
        return '#f39c12';
      case 'case':
        return '#9b59b6';
      case 'voicemail':
        return '#2ecc71';
      case 'callback':
        return '#e67e22';
      case 'system':
        return '#6c757d';
      default:
        return '#95a5a6';
    }
  };

  return (
    <Dialog width="600px" UNSAFE_className="ihub-notifications-custom-dialog">
      <Content UNSAFE_className="ihub-notifications-content">
        <Flex direction="column" height="600px" flex={1}>
          {/* Fixed Header */}
          <Flex direction="row" margin="16px" justifyContent="space-between" alignItems="center">
            <Text UNSAFE_style={{ font: "normal normal normal 16px/21px Adobe Clean" }}>Notifications</Text>
            <Flex direction="row" gap="size-200">
              <ActionButton 
                onPress={handleMarkAllAsRead}
                isDisabled={notifications.notifications.length === 0}
              >
                Mark All Read
              </ActionButton>
              <ActionButton 
                onPress={handleClearAll}
                isDisabled={notifications.notifications.length === 0}
              >
                Clear All
              </ActionButton>
            </Flex>
          </Flex>
          <Divider size="S" />
          
          {/* Scrollable Content */}
          <Flex 
            direction="column" 
            flex={1} 
            UNSAFE_style={{ 
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: 0
            }}
          >
            <Flex margin="16px" direction="column">
              {notifications.notifications.length === 0 ? (
                <Flex direction="column" alignItems="center" justifyContent="center" margin="15px 16px 12px 16px">
                  <Text UNSAFE_style={{ font: "normal normal normal 16px/21px Adobe Clean" }}>No notifications</Text>
                </Flex>
              ) : (
                notifications.notifications.map((notification: any) => (
                  <div className="ihub-notification-item-container" key={notification.id} onClick={() => !notification.isRead && handleMarkAsRead(notification.id)}>
                    <Flex direction="row" UNSAFE_className={`ihub-notification-item ${notification.isRead ? 'read' : 'unread'}`} alignItems="start" gap="size-200">
                      <Flex direction="row" alignItems="start" gap="size-200">
                        {/* <Icon size="S" color={getNotificationColor(notification.type)}>{getNotificationIcon(notification.type)}</Icon> */}
                        <div style={{
                          fontSize: '20px',
                          color: getNotificationColor(notification.type),
                          flexShrink: 0,
                          marginTop: '2px'
                        }}>
                          {getNotificationIcon(notification.type, notification.category)({ color: getNotificationColor(notification.type) })}
                        </div>
                      </Flex>
                      <Flex direction="column" flex={1} minWidth={0}>
                        <Text UNSAFE_style={{ font: "normal normal bold 16px/21px Adobe Clean", color: notification.isRead ? '#666' : '#000000' }}>{notification.title}</Text>
                        <Text UNSAFE_style={{ font: "normal normal normal 16px/20px Adobe Clean", color: notification.isRead ? '#666' : '#222222' }}>{notification.description}</Text>
                        <Text UNSAFE_style={{ font: "normal normal normal 14px/21px Adobe Clean", color: notification.isRead ? '#666' : '#505050' }}>{formatTimestamp(notification.timestamp)}</Text>
                      </Flex>
                      <ActionButton 
                        isQuiet 
                        onPress={() => handleRemoveNotification(notification.id)}
                        UNSAFE_style={{ minWidth: 'auto' }}
                        aria-label="Remove notification"
                      >
                        <Close size="S" />
                      </ActionButton>
                    </Flex>
                  </div>
                ))
              )}
            </Flex>
          </Flex>
          
          {/* Fixed Footer */}
          {notifications.unreadCount > 0 && (
            <Flex direction="column" alignItems="center" justifyContent="center" UNSAFE_style={{ backgroundColor: '#f8f9fa', padding: '12px 16px', borderTop: '1px solid #e0e0e0' }}>
              <Text UNSAFE_style={{ font: "normal normal normal 16px/21px Adobe Clean" }}>{notifications.unreadCount} unread notification{notifications.unreadCount !== 1 ? 's' : ''}</Text>
            </Flex>
          )}
        </Flex>
      </Content>
    </Dialog>
  );
};

export default NotificationsPanel;
